"use client";
import { useLayoutEffect, useState, useRef } from "react";
import { Instructor } from "@/common/instructor/types";
import Image from "next/image";
import Link from "next/link";
import { BadgeIcon } from "../ui/badgeIcon";
import useElementEvents from "@/hooks/eventing/useElementEvents";
import sanitizeAndParseInlineHTML from "@/utils/functions/sanitizeAndParseInlineHTML";

type InstructorCardProps = {
  instructor: Instructor;
};

export default function InstructorCard({ instructor }: InstructorCardProps) {
  const [shouldReadMore, setShouldReadMore] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const rawAboutMe = instructor.aboutMe || "";
  const aboutMeWithParagraphs = /<p[\s>]/i.test(rawAboutMe)
    ? rawAboutMe
    : `<p>${rawAboutMe}</p>`;

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el || !rawAboutMe) {
      setShouldReadMore(false);
      return;
    }
    setShouldReadMore(el.scrollHeight > el.clientHeight);
  }, [aboutMeWithParagraphs, rawAboutMe]);

  const { elementClicked } = useElementEvents({
    webElement: {
      elementType: "featured-instructor",
      name: "featured-instructor",
      location: "why-learners-choose",
      text: "featured-instructor",
      htmlId: "featured-instructor",
    },
  });

  const fullName = `${instructor.firstName}${instructor.lastName ? ` ${instructor.lastName}` : ""}`;
  const org =
    instructor.organizationOverride ||
    instructor.partnerName ||
    instructor.partners?.[0]?.name ||
    "";

  return (
    <div className="rounded-xl border-2 bg-white p-8 h-full">
      <BadgeIcon
        icon="star-outline"
        withBorder={false}
        iconSize={14}
        className="mb-6"
      >
        Featured Instructor
      </BadgeIcon>

      <div className="flex flex-col md:flex-row gap-4 md:items-start">
        {instructor.imageUrl && (
          <div className="flex flex-shrink-0 items-center justify-center w-[90px] h-[90px] rounded-full border-2 overflow-hidden">
            <Image
              src={instructor.imageUrl}
              alt={fullName}
              title={fullName}
              width={90}
              height={90}
              className="rounded-full object-cover w-full h-full"
            />
          </div>
        )}

        <div>
          <p className="text-xl font-semibold text-elm-500 mb-1">{fullName}</p>
          {(instructor.position || org) && (
            <p className="text-base font-bold text-gray-800 mb-0 leading-[24px]">
              {instructor.position}
              {instructor.position && org && " at "}
              {org}
            </p>
          )}
        </div>
      </div>

      {rawAboutMe && (
        <div
          className="text-base text-gray-800 mt-8 mb-6 line-clamp-2 text-ellipsis [&_p]:mb-0 [&_p]:text-base"
          ref={containerRef}
        >
          {sanitizeAndParseInlineHTML(aboutMeWithParagraphs)}
        </div>
      )}

      {rawAboutMe && shouldReadMore && (
        <Link
          href={`/bio/${instructor.slug}`}
          target="_blank"
          className="flex flex-start text-sm md:text-base underline cursor-pointer ml-auto w-fit"
          onClick={() => elementClicked()}
        >
          Read More
        </Link>
      )}
    </div>
  );
}
